import * as React from "react";
import {connect, Dispatch} from "react-redux";
import {OnFailure, OnSuccess} from "../../../store/action/actionHelpers";
import {RootState} from "../../../store/reducer/root/RootState";
import DictionarySortableItemsList from "../common/DictionarySortableItemsList";
import {createMerchant, fetchMerchants, updateMerchant, updateMerchantsSortOrder} from "../../../store/action/merchant/merchant";
import {none, option, Option} from "ts-option";
import Loading from "../../ui/loading/Loading";
import {isDefined, notifyBugsnag, notifyBugsnagAndShowToast} from "../../../utils/Utils";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import {api} from "../../../api/api";
import Toast from "../../helper/Toast";
import UploadMerchantDataFileModal from "./modal/UploadMerchantDataFileModal";
import "./merchantConfigView.scss";
import Merchant from "../../../model/merchant/Merchant";
import DateUtils from "../../../utils/DateUtils";
import {fetchSites} from "../../../store/action/site/site";
import _ = require("underscore");


interface ReduxProps {
	/**
	 * All merchants.
	 */
	merchants: Option<Merchant[]>;
}

interface DispatchProps {
	/**
	 * Sort order update callback.
	 * @param {Merchant[]} merchants
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 * @returns {any}
	 */
	updateSortOrder: (merchants: Merchant[], onSuccess: OnSuccess, onFailure: OnFailure) => any;
	/**
	 * Create merchant.
	 * @param {Merchant} merchant
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 * @returns {any}
	 */
	createMerchant: (merchant: Merchant, onSuccess: OnSuccess, onFailure: OnFailure) => any;
	/**
	 * Update merchant.
	 * @param {string} merchantId
	 * @param {Merchant} merchant
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 * @returns {any}
	 */
	updateMerchant: (merchantId: string, merchant: Merchant, onSuccess: OnSuccess, onFailure: OnFailure) => any;
	/**
	 * Fetch merchants.
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchMerchants: (onSuccess: OnSuccess, onFailure: OnFailure) => void
	/**
	 * Fetch sites.
	 * @param {OnFailure} onFailure
	 */
	fetchSites: (onFailure: OnFailure) => void
}

interface State {
	isLoading: boolean
	uploadingMerchantId: Option<string>
	downloadLoading: { [key: string]: boolean }
	typeOfData: Option<TypeOfData>
}

type Props = ReduxProps & DispatchProps;

/**
 * Merchant config view.
 */
class MerchantConfigView extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {isLoading: props.merchants.isEmpty, uploadingMerchantId: none, downloadLoading: {}, typeOfData: none};
		this.stopDownloadLoading = this.stopDownloadLoading.bind(this);
		this.renderRowBody = this.renderRowBody.bind(this);
		this.fetchAdditionalData = this.fetchAdditionalData.bind(this);
	}

	componentDidMount() {
		if (this.props.merchants.isEmpty) {
			this.fetchMerchants()
		}
	}

	render() {
		return (
			<>
				<Loading isLoading={this.state.isLoading}>
					<DictionarySortableItemsList title={"Merchant"}
												 items={this.props.merchants.getOrElse([])}
												 updateItem={this.props.updateMerchant}
												 createItem={this.props.createMerchant}
												 updateSortOrder={this.props.updateSortOrder}
												 renderHeaderBody={this.renderHeaderBody}
												 renderRowBody={this.renderRowBody}
												 errorHandler={this.nameErrorHandler}/>
				</Loading>
				{this.renderModal()}
			</>
		)
	}

	renderModal() {
		return (
			this.state.uploadingMerchantId.isDefined && this.state.typeOfData.isDefined &&
			<UploadMerchantDataFileModal merchants={this.props.merchants}
										 merchantId={this.state.uploadingMerchantId.get}
										 onClose={() => this.setState({uploadingMerchantId: none, typeOfData: none})}
										 typeOfData={this.state.typeOfData.get}
										 onSuccess={this.fetchAdditionalData}/>
		)
	}

	fetchMerchants() {
		this.setState({isLoading: true},
			() => this.props.fetchMerchants(() => this.setState({isLoading: false}),
				(e) => notifyBugsnagAndShowToast(e, "Fetch merchants")))
	}

	/**
	 * Fetch sites
	 */
	fetchSites() {
		this.props.fetchSites(notifyBugsnag)
	}

	/**
	 * Fetch new data after successful upload
	 */
	fetchAdditionalData(typeOfData: TypeOfData) {
		if (typeOfData == TypeOfData.branchLocations) {
			this.fetchMerchants();
			this.fetchSites();
		}
	}

	renderHeaderBody() {
		return (
			<>
				<th>No</th>
				<th>Name</th>
				<th>Materials</th>
				<th>Branch locations</th>
				<th>Last branch locations upload</th>
			</>
		)
	}

	renderRowBody(merchant: Merchant, index: number) {
		const lastUpdateDate = merchant.branchesLastUpdateDate.map(date => date.format(DateUtils.DATE_TIME_FORMAT)).getOrElse("");
		return (
			<>
				{this.renderCell(index + 1)}
				{this.renderCell(merchant.name)}
				{this.renderCell(this.renderImportExportButtons(merchant, TypeOfData.materials))}
				{this.renderCell(this.renderImportExportButtons(merchant, TypeOfData.branchLocations))}
				{this.renderCell(lastUpdateDate)}
			</>
		)
	}

	renderCell(value: JSX.Element | string | number) {
		return (
			<td style={{width: "20%"}}>{value}</td>
		)
	}

	renderImportExportButtons(merchant: Merchant, typeOfData: TypeOfData) {
		return (
			<div className={"c-merchant-table_buttons"}>
				<ButtonPrimary label={"Upload"}
							   onClick={() => this.onUpload(merchant, typeOfData)}/>
				<ButtonPrimary label={"Download"}
							   disabled={isDefined(this.state.downloadLoading[merchant.id + typeOfData])}
							   onClick={_.debounce(() => this.downloadMerchantFile(merchant, typeOfData), 300, false)}/>
			</div>
		)
	}

	onUpload(merchant: Merchant, typeOfData: TypeOfData) {
		this.setState({uploadingMerchantId: option(merchant.id), typeOfData: option(typeOfData)})
	}

	downloadMerchantFile(merchant: Merchant, typeOfData: TypeOfData) {
		const loadingState = this.state.downloadLoading;
		loadingState[merchant.id + typeOfData] = true;
		this.setState({downloadLoading: loadingState},
			() => api.instance.merchantApi.downloadMerchantFile(merchant, typeOfData)
				.then(() => this.stopDownloadLoading(merchant, typeOfData))
				.catch((err: any) => {
					this.stopDownloadLoading(merchant, typeOfData);
					if (err && err.response && err.response.data.status == 404) {
						Toast.error(`Merchant ${typeOfData} data not found.`, `Merchant ${typeOfData} download`);
					}
					else {
						notifyBugsnagAndShowToast(err, `Merchant ${typeOfData} download`);
					}
				})
		)
	}

	stopDownloadLoading(merchant: Merchant, typeOfData: TypeOfData) {
		const loadingState = this.state.downloadLoading;
		delete loadingState[merchant.id + typeOfData];
		this.setState({downloadLoading: loadingState})
	}

	nameErrorHandler(error: any): string | null {
		if (error && error.response && error.response.data.status == 409
			&& error.response.data.exception == "MerchantWithNameAlreadyExistsException") {
			return `Merchant with given name already exists`;
		}
		return null
	}
}

export enum TypeOfData {
	materials = "materials",
	branchLocations = "locations"
}

function mapStateToProps(state: RootState) {
	return {
		merchants: state.merchant.merchants
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		updateSortOrder: (merchants: Merchant[], onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			updateMerchantsSortOrder(merchants, onSuccess, onFailure)),
		createMerchant: (merchant: Merchant, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			createMerchant(merchant, onSuccess, onFailure)),
		updateMerchant: (merchantId: string, merchant: Merchant, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			updateMerchant(merchantId, merchant, onSuccess, onFailure)),
		fetchMerchants: (onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(fetchMerchants(onSuccess, onFailure)),
		fetchSites: (onFailure: OnFailure) => dispatch(fetchSites(() => {}, onFailure))
	}
}

export default connect<ReduxProps, DispatchProps, null>(mapStateToProps, mapDispatchToProps)(MerchantConfigView)